// The camera's opening move: out of the gate, down onto the system in one dive, then one
// full counter-clockwise circuit of the sun that lands on the camera's home, with the sun
// held at the centre of frame the whole way. Pure geometry — no three.js — so it is
// testable on its own.
//
// It is one path driven by one tween on purpose. It used to be two: a straight dive to a
// fixed point, then a separate corkscrew circuit. Both eases came to rest where they met,
// so the camera stopped dead out on the diagonal and set off again facing almost the
// opposite way. One path has no hand-over to get wrong.
//
// The path is polar around the sun, and the shape has to hold two things at once:
//
//   - The radius only ever falls, from the gate to the home. No weaving in and out: the
//     dive becomes a spiral, closing on the sun the whole way.
//   - The join between the dive and the circuit is an apex, not a corner. The azimuth is
//     flat there, so the camera arrives and leaves heading exactly at the sun, and because
//     the radius is still falling the speed through it is not zero. Continuous heading,
//     continuous speed, no stop.

import { diagonalHomePosition, HOME_DISTANCE, LOBBY_CAMERA_START } from './cameraHome';

/**
 * How long the whole move takes, dive and circuit together. The dive takes a quarter of
 * it, which is what the old door flight cost on its own, so the circuit keeps its twelve
 * seconds and the extra goes on the dive's easing.
 */
export const OPENING_MOVE_DURATION = 16;

/** Where the dive hands over to the circuit, as a fraction of the move. */
export const JOIN_PROGRESS = 0.25;

/**
 * The slope the ramp in `launchEase` starts on, as a fraction of the move's average speed. With
 * the pull-out key below doing the work of getting the camera out of the doorway, this is now
 * purely a shaping knob for how long that takes: at 0.25 the camera needs about 0.76 s to clear
 * the doorway where without the ramp it needs 0.22 s. It is a fraction of the move's average
 * rather than a rate, so it keeps its meaning if the path is retuned.
 */
export const GATE_SPEED = 0.25;

/**
 * The ramp laid over the path — a blend from linear toward quadratic, so its slope starts at
 * GATE_SPEED and rises from there. It is 0 at 0 and 1 at 1, so the path's own start and end
 * are untouched and the landing is still the path's own flat one.
 *
 * LobbyContainer applies this to the tween's *linear* progress by hand rather than handing
 * it to gsap as an `ease`. gsap's ease moves `tween.ratio` and leaves `tween.progress()`
 * linear whatever ease is set, so the `ease: 'power2.inOut'` that used to sit on that tween
 * was read by nobody — the tween ran linear and the slower start it was added for never
 * happened. Keeping the ramp here means it is a plain function the tests can hold.
 *
 * Since the pull-out key below, this ramp's job is narrower than it was: the path itself now
 * gets the camera out of the doorway slowly, so the ramp no longer has to invent a slow start
 * out of a path that began at full slope. What it does now is stretch that pull-out in time —
 * 0.76 s through the doorway against 0.22 s without it — and it still leaves the landing alone.
 */
export function launchEase(progress) {
    return progress * (GATE_SPEED + (1 - GATE_SPEED) * progress);
}

/**
 * The circuit's outer ring, and the radius the dive ends on. Well outside the system: the
 * outermost planet the circuit is built around is Saturn at 154.5, and Uranus's orbit at
 * 304.5 sits just above this.
 */
export const APOGEE_DISTANCE = 300;

/**
 * Where along the move the camera comes square to the two planets it is built around.
 * The circuit sweeps a full turn counter-clockwise from the home diagonal, so it crosses
 * Saturn's azimuth (63.4075°) 5.1133% of the way round and Jupiter's (325.5852°) 77.9403%
 * of the way round — the azimuths Planets.js's layout actually puts them at, with the
 * Sun consuming the first step of its `angle *= angleOffset` cascade. Clockwise instead
 * would cross neither in the open: it would reach Saturn's azimuth only at the very end,
 * at a radius inside Saturn's orbit, i.e. through the ring.
 *
 * Both are solved from the circuit's own azimuth curve rather than rounded to taste, so
 * the camera really is lined up on the sun and the planet — which is what puts the planet
 * in the middle of the frame.
 */
export const SATURN_PASS = 0.352716;
export const JUPITER_PASS = 0.772273;

/**
 * How far outside each planet's orbit the camera passes it. Both are what frames them:
 * the camera aims at the sun, so a planet is square in the middle of the frame exactly
 * when the camera, the sun and the planet are in line — which is the pass, and it is
 * only worth much if the planet is close enough to fill a decent part of the frame.
 *
 * Jupiter's had to grow from 46. The radius of this move only ever falls and its last
 * key is the home at 144.2734, so a pass at 89.5 + 46 = 135.5 is simply unreachable now:
 * the camera would have to come back out to it. 60 puts the pass at 149.5, above the
 * home and clear of the planet's own 6.4.
 */
export const JUPITER_PASS_DISTANCE = 60;

/** How far outside Saturn's orbit the camera passes it. Its ring reaches 10. */
export const SATURN_PASS_DISTANCE = 50.5;

/** How far out Jupiter and Saturn sit, from the layout in Planets.js (sunRad = 14.5). */
const JUPITER_ORBIT = 75 + 14.5;
const SATURN_ORBIT = 140 + 14.5;

/** Where the camera waits for the doors: far out on +Z, looking straight at the sun. */
const GATE_DISTANCE = Math.hypot(LOBBY_CAMERA_START.x, LOBBY_CAMERA_START.z);
const GATE_AZIMUTH = Math.atan2(LOBBY_CAMERA_START.z, LOBBY_CAMERA_START.x);

/**
 * The diagonal the circuit runs from and lands on — the one the camera's home sits on, so
 * moving the home moves the lap with it. (It is the camera's resting yaw's number too, but
 * it is a different quantity: that one is where the camera looks from, this is where it
 * sits.)
 */
const home = diagonalHomePosition();
const HOME_AZIMUTH = Math.atan2(home.z, home.x);

/**
 * The dive, keyed to progress: from the end of the pull-out down to the apogee, 9700 units
 * in a quarter of the move. These intermediate keys are what keep the join from braking.
 * With the dive as one segment it carries the whole descent at one average slope, so it
 * arrives at the join about twenty times faster than the circuit leaves it — no corner in
 * it, but a visible stop at the knot. These keys hold the deceleration roughly constant
 * instead, |dr/dp| falling from about 88 000 at the dive's first key to about 1 800 at the
 * join, so the camera eases off across the whole dive rather than all at the end. The radius
 * still only falls.
 */
const PLUNGE_KEYS = [
    [0.05, 6588],
    [0.1, 3912],
    [0.15, 1972],
    [0.2, 768],
    [0.235, 363.1],
];

/**
 * The pull-out: how far the camera drifts clear of the doorway before the dive starts, and how
 * far along the move that happens.
 *
 * This is a path key rather than a faster or slower launch because the geometry is what is
 * wrong, not the easing. The gate is placed five units in front of the camera and the move
 * starts at radius 10000, so the doorway is a twentieth of one percent of the path; the dive's
 * own slope at the gate is about 68 000 units per unit of progress, which puts the doorway
 * behind the camera in **5 ms** — under a third of one 60 Hz frame. The lobby did not fly away
 * so much as get deleted, and no ease can slow that down: the ramp is only ~0.25 of the slope
 * at the start, and 0.25 of *instant* is still instant.
 *
 * With this key the camera takes ~0.76 s to clear the doorway and ~1.4 s to be 20 units clear,
 * then the plunge picks up. Nothing else moves — the plunge keys, the join and the landing are
 * untouched, and the join lands at the same place at the same 6.95 s of the move.
 */
const EXIT_PROGRESS = 0.03;
const EXIT_DISTANCE = 25;

/**
 * The move's distance from the sun. Strictly falling: every key is inside the one before
 * it, and the monotone curve below cannot overshoot a key, so the camera never backs out.
 * It leaves the gate at rest — the doors are at a dead stop when the move begins, and a pull
 * is felt from rest — creeps the first 25 units clear of the doorway (see EXIT_PROGRESS),
 * and then falls at the dive's own slope; it lands at rest.
 */
const RADIUS_KEYS = [
    [0, GATE_DISTANCE, true],
    [EXIT_PROGRESS, GATE_DISTANCE - EXIT_DISTANCE],
    ...PLUNGE_KEYS,
    [JOIN_PROGRESS, APOGEE_DISTANCE],
    [SATURN_PASS, SATURN_ORBIT + SATURN_PASS_DISTANCE],
    [JUPITER_PASS, JUPITER_ORBIT + JUPITER_PASS_DISTANCE],
    [1, HOME_DISTANCE, true],
];

/**
 * The move's azimuth. It leaves the gate along -Z, which is azimuth 90°, and turns a
 * quarter turn clockwise onto the home diagonal; from there the circuit sweeps a full turn
 * counter-clockwise, ending on 405°, which is the same place as the 45° it left.
 *
 * Every join between the keys is flat, and that is the whole trick. Flat at the gate, and
 * held flat through the pull-out, means the camera leaves along the straight line the doors
 * opened onto. Flat at the end means it settles rather than cutting. Flat at the join — where
 * the dive meets the circuit — means the camera is heading exactly at the sun as the circuit
 * takes over, the one heading both halves can share; and because the radius is still falling
 * there, the speed through it is not zero.
 *
 * The hold at the dive's first key is the fourth key, and it is there for the pull-out. Without
 * it the quarter turn starts the instant the camera does, and because the radius is pinned flat
 * over the pull-out the camera spends it sliding sideways: measured, the heading swings from
 * about 85° off the sun axis to about 26° across the end of the pull-out — a 21° kink in a
 * single step, against a whole-move contract of less than one degree. Holding the azimuth
 * straight makes the exit a straight line through the doorway, which is what an exit is meant
 * to be, and the quarter turn then happens over the dive where it belongs.
 *
 * The hold runs to `PLUNGE_KEYS[0][0]` rather than stopping at EXIT_PROGRESS, and that length
 * is measured rather than chosen: ending it exactly where the radius slope jumps (about 833
 * units per unit of progress on the pull-out, then about 170 000 on the dive) leaves the
 * heading turning 1.0005° in that one step — the tangential drift has barely started while the
 * radial term has already exploded, so the ratio swings fast. Running the straight line on to
 * the dive's first key puts that transition where the radius is already fast and smooth, and
 * the worst step in the whole move drops to 0.25°.
 */
const AZIMUTH_KEYS = [
    [0, GATE_AZIMUTH, true],
    [PLUNGE_KEYS[0][0], GATE_AZIMUTH, true],
    [JOIN_PROGRESS, HOME_AZIMUTH, true],
    [1, HOME_AZIMUTH + 2 * Math.PI, true],
];

/**
 * Fritsch–Carlson monotone cubic through `[progress, value, flat]` keys: each tangent is
 * the weighted mean of the slopes either side of its key, then clamped so the curve can
 * never overshoot a key. Overshoot is the thing to rule out here — a radius that
 * overshoots is a radius that backs out again, which is exactly the weaving this move must
 * not do. `flat` pins a tangent to zero, which is how the move lands at rest, how the azimuth
 * leaves along the straight line the doors opened onto, and how the radius begins its slow pull
 * out of the doorway; see EXIT_PROGRESS.
 */
function monotoneCurve(keys) {
    const spans = [];
    const slopes = [];

    for (let i = 0; i < keys.length - 1; i++) {
        spans.push(keys[i + 1][0] - keys[i][0]);
        slopes.push((keys[i + 1][1] - keys[i][1]) / spans[i]);
    }

    const tangents = keys.map(([, , flat], i) => {
        if (flat) return 0;
        if (i === 0) return slopes[0];
        if (i === keys.length - 1) return slopes[slopes.length - 1];

        const before = slopes[i - 1];
        const after = slopes[i];

        if (before * after <= 0) return 0;

        const weightBefore = 2 * spans[i] + spans[i - 1];
        const weightAfter = spans[i] + 2 * spans[i - 1];

        return (weightBefore + weightAfter) / (weightBefore / before + weightAfter / after);
    });

    for (let i = 0; i < slopes.length; i++) {
        if (slopes[i] === 0) {
            tangents[i] = 0;
            tangents[i + 1] = 0;

            continue;
        }

        const out = tangents[i] / slopes[i];
        const into = tangents[i + 1] / slopes[i];
        const steepness = out * out + into * into;

        if (steepness > 9) {
            const limit = 3 / Math.sqrt(steepness);

            tangents[i] = limit * out * slopes[i];
            tangents[i + 1] = limit * into * slopes[i];
        }
    }

    return (progress) => {
        if (progress <= keys[0][0]) return keys[0][1];

        for (let i = 0; i < spans.length; i++) {
            if (progress > keys[i + 1][0]) continue;

            const t = (progress - keys[i][0]) / spans[i];
            const t2 = t * t;
            const t3 = t2 * t;

            return keys[i][1] * (2 * t3 - 3 * t2 + 1)
                + spans[i] * tangents[i] * (t3 - 2 * t2 + t)
                + keys[i + 1][1] * (3 * t2 - 2 * t3)
                + spans[i] * tangents[i + 1] * (t3 - t2);
        }

        return keys[keys.length - 1][1];
    };
}

const radiusAt = monotoneCurve(RADIUS_KEYS);
const azimuthAt = monotoneCurve(AZIMUTH_KEYS);

/** Where the camera is, `progress` of the way through the opening move. */
export function flyByPoint(progress) {
    const radius = radiusAt(progress);
    const azimuth = azimuthAt(progress);

    return {
        x: radius * Math.cos(azimuth),
        y: 0,
        z: radius * Math.sin(azimuth),
    };
}
